import DownloadIcon from '@mui/icons-material/Download'
import {
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  MenuItem,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { api } from '@/api/client'
import {
  useConsolidatedProduction,
  useMenuCategories,
  useKitchenAction,
  useKitchenBoard,
  useSessionTypes,
} from '@/api/hooks'
import { useAuthStore } from '@/auth/authStore'
import {
  EmptyState,
  ErrorBanner,
  formatDateTime,
  formatNumber,
  humanise,
  isoDate,
  PageHeader,
  StatusChip,
} from '@/components/common'

const NEXT_STATUS: Record<string, string> = {
  APPROVED: 'PREPARATION_STARTED',
  PREPARATION_STARTED: 'READY',
  PARTIALLY_READY: 'READY',
  READY: 'DISPATCHED',
  DISPATCHED: 'RECEIVED_AT_VENUE',
  RECEIVED_AT_VENUE: 'CLOSED',
}

export function KitchenPage() {
  const can = useAuthStore((state) => state.can)
  const canWrite = useAuthStore((state) => state.canWrite)
  const [productionDate, setProductionDate] = useState(() => isoDate(0))
  // The day's board is more than a shift can work from, so it narrows: the
  // branch cooking it, the hall it goes to, and the course being worked on.
  const [toDate, setToDate] = useState(() => isoDate(0))
  // The branch comes from the toolbar, not from a second picker here.
  const outletId = useAuthStore((state) => state.activeOutletId) ?? undefined
  const [categoryId, setCategoryId] = useState('')
  // A kitchen works a sitting at a time: the breakfast is out of the door
  // before the lunch is started, and a board carrying both is read twice.
  const [mealPeriod, setMealPeriod] = useState('')

  // The board covers the same window as the consolidated list beside it;
  // otherwise the two panels quietly disagree about which days they mean.
  const { data: requests, isLoading, error } = useKitchenBoard({
    from: productionDate,
    to: toDate || productionDate,
    mealPeriod: mealPeriod || undefined,
  })
  const { data: sessions } = useSessionTypes()
  const { data: categories } = useMenuCategories()
  const { data: consolidated } = useConsolidatedProduction({
    productionDate,
    toDate: toDate || undefined,
    outletId,
    categoryId: categoryId || undefined,
    mealPeriod: mealPeriod || undefined,
  })
  const action = useKitchenAction()

  return (
    <>
      <PageHeader
        title="Kitchen"
        subtitle="Consolidated production for the day, traceable back to each event"
        actions={
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              label="From"
              type="date"
              value={productionDate}
              onChange={(event) => setProductionDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              label="To"
              type="date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 165 }}
            />
            <TextField
              select
              size="small"
              label="Session"
              value={mealPeriod}
              onChange={(event) => setMealPeriod(event.target.value)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">All sittings</MenuItem>
              {(sessions ?? []).map((session) => (
                <MenuItem key={session.code} value={session.code}>
                  {session.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Category"
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">All courses</MenuItem>
              {(categories ?? []).map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() =>
                api.download(
                  '/api/v1/reports/KITCHEN_PRODUCTION_SHEET/export',
                  { from: productionDate, to: productionDate },
                  `production_${productionDate}.xlsx`,
                )
              }
            >
              Production sheet
            </Button>
          </Stack>
        }
      />

      <ErrorBanner error={error ?? action.error} />

      <Grid container spacing={2}>
        <Grid item xs={12} lg={7}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Production request
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Total to cook per item across every event in the window, from the pax, the package,
                the dishes chosen and the sides that go out with them.
              </Typography>
              {!consolidated || consolidated.length === 0 ? (
                <EmptyState message="Nothing to produce on this date." />
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell align="right">Required</TableCell>
                      <TableCell align="right">Produced</TableCell>
                      <TableCell align="right">Events</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {consolidated.map((item) => (
                      <TableRow key={item.menuItemId} hover>
                        <TableCell>{item.itemName}</TableCell>
                        <TableCell align="right">
                          <strong>{formatNumber(item.totalQuantity)}</strong>{' '}
                          <Typography variant="caption" color="text.secondary">
                            {humanise(item.unit)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{formatNumber(item.producedQuantity)}</TableCell>
                        <TableCell align="right">
                          <Chip size="small" variant="outlined" label={item.eventCount} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Card sx={{ height: '100%' }}>
            {isLoading && <LinearProgress />}
            <CardContent>
              {/* The same work seen the other way round: what to cook is on
                  the left, and this is each event's request moving through
                  approval, cooking and dispatch. */}
              <Typography variant="subtitle1" gutterBottom>
                By event
              </Typography>
              {!requests || requests.length === 0 ? (
                <EmptyState
                  message="No kitchen requests for this date."
                  hint="Requests are generated automatically when an order is confirmed."
                />
              ) : (
                <Stack spacing={2}>
                  {requests.map((request) => (
                    <Card key={request.id} variant="outlined">
                      <CardContent>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          justifyContent="space-between"
                          alignItems={{ sm: 'center' }}
                          spacing={1}
                          sx={{ mb: 1.5 }}
                        >
                          <div>
                            {/* Whose event it is, first. A chef asked about
                                "the Iyer wedding" cannot find it under a
                                request number, and the desk never rings up
                                quoting one. */}
                            <Typography variant="subtitle2">
                              {request.order?.customerName ?? request.requestNumber}
                              {request.order?.sessionName ? ` · ${request.order.sessionName}` : ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {[
                                request.order?.orderNumber,
                                request.order?.eventType,
                                request.mealPeriod ? humanise(request.mealPeriod) : null,
                                request.order?.venue,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatNumber(request.guestCount)} guests · required{' '}
                              {formatDateTime(request.requiredAt)}
                              {request.hasLiveCounter ? ' · live counter' : ''}
                              {request.order?.customerName ? ` · ${request.requestNumber}` : ''}
                            </Typography>
                          </div>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <StatusChip status={request.status} />
                            {can('kitchen.approve') && request.status === 'PLANNED' && (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => action.mutate({ id: request.id, action: 'approve' })}
                              >
                                Approve
                              </Button>
                            )}
                            {canWrite('kitchen') && NEXT_STATUS[request.status] && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() =>
                                  action.mutate({
                                    id: request.id,
                                    action: 'status',
                                    body: { status: NEXT_STATUS[request.status] },
                                  })
                                }
                              >
                                Move to {humanise(NEXT_STATUS[request.status])}
                              </Button>
                            )}
                          </Stack>
                        </Stack>

                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Item</TableCell>
                              <TableCell align="right">Required</TableCell>
                              <TableCell align="right">Produced</TableCell>
                              <TableCell align="right">Dispatched</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {request.items.map((item) => (
                              <TableRow key={item.id}>
                                <TableCell>{item.itemName}</TableCell>
                                <TableCell align="right">
                                  {formatNumber(item.requiredQuantity)} {humanise(item.unit)}
                                </TableCell>
                                <TableCell align="right">{formatNumber(item.producedQuantity)}</TableCell>
                                <TableCell align="right">
                                  {formatNumber(item.dispatchedQuantity)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  )
}
